<template>
    <div>
        <v-card>
            <v-toolbar
                flat
                color="primary"
                dark
                dense
            >
                <v-icon left>mdi-card-text</v-icon>
                Reusable Fieldtypes
                <v-spacer />
                <v-btn
                    round
                    color="success"
                    :to="{name: 'OrgSelectedFieldtype', params: {id: 'new'}}"
                >
                    <v-icon left>add</v-icon>
                    New Fieldtype
                </v-btn>
            </v-toolbar>
            <v-card-text>
                <v-layout
                    row
                    wrap
                >
                    <v-flex
                        sm3
                        style="display: flex;flex-direction: column;"
                    >
                        <v-text-field
                            v-model="search"
                            box
                            hide-details
                            :loading="loading"
                            label="Search Fieldtypes"
                            clearable
                            style="flex-grow: 0;"
                        />
                        <v-list
                            three-line
                            style="background: rgba(0,0,0,.03);border-radius: 5px;flex-grow: 1;"
                        >
                            <v-list-tile
                                v-for="ft in fieldtypes"
                                :key="ft.id"
                                :to="{name: 'OrgSelectedFieldtype', params: {id: ft.id}}"
                            >
                                <v-list-tile-content>
                                    <v-list-tile-title v-html="ft.name"></v-list-tile-title>
                                    <v-list-tile-sub-title v-html="ft.desc"></v-list-tile-sub-title>
                                </v-list-tile-content>
                            </v-list-tile>
                        </v-list>
                        <v-card-actions>
                            <v-spacer />
                            <v-btn
                                :disabled="!canPrev"
                                icon
                                @click="goPage(-1)"
                            >
                                <
                            </v-btn>
                            &nbsp;
                            {{page * 10 - 9}}
                            -
                            {{page * 10 > total ? total : page * 10}}
                             of 
                            {{total}}
                            &nbsp;
                            <v-btn
                                :disabled="!canNext"
                                icon
                                @click="goPage(1)"
                            >
                                >
                            </v-btn>
                            <v-spacer />
                        </v-card-actions>
                    </v-flex>
                    <v-flex
                        sm9
                    >
                        <v-card
                            v-if="focusFieldtype || loadingFt"  
                            style="margin: 0 10px 0 10px;"
                        >
                            <v-toolbar
                                dense
                                flat
                            >
                                Fieldtype Editor
                                <v-spacer />
                                <v-btn
                                    small
                                    @click="$router.replace({name: 'OrgFieldtype'})"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    :disabled="!changed || saving"
                                    color="primary"
                                    small
                                    @click="saveFt()"
                                >
                                    Save Changes
                                </v-btn>
                            </v-toolbar>

                            <div
                                v-if="focusFieldtype && !loadingFt && focusFieldtype.questions"
                                style="background: rgb(250, 250, 250);width: 100%;padding:10px;padding-left:25px;font-size: 14px;color:black;"
                            >
                                <span
                                    v-if="focusFieldtype.questions.length > 0"
                                >
                                    This fieldtype is used in {{focusFieldtype.questions.length}} questions across {{templateCount(focusFieldtype.questions)}} templates. <a @click="questionDialog = !questionDialog">See Where</a>
                                </span>
                                <span
                                    v-else
                                >
                                    This fieldtype is not used in any template.
                                </span>
                            </div>

                            <v-card-text
                                v-show="loadingFt"
                            >
                                <div
                                    style="padding: 25px;margin:auto"
                                >
                                    <v-progress-linear
                                        indeterminate
                                        query
                                    />
                                </div>
                            </v-card-text>
                            <v-card-text
                                v-show="!loadingFt"
                            >
                                <v-text-field
                                    v-model="focusFieldtype.name"
                                    label="Name"
                                />
                                <v-text-field
                                    v-model="focusFieldtype.desc"
                                    label="Description"
                                />
                                <v-select
                                    v-model="focusFieldtype.atomic_type"
                                    :items="atomicTypeOptions"
                                    :item-value="'value'"
                                    :item-text="'name'"
                                    label="Field Type"
                                    :disabled="!!focusFieldtype.id"
                                />
                                <v-checkbox
                                    v-if="focusFieldtype.atomic_type"
                                    v-model="focusFieldtype.required"
                                    label="Required"
                                />
                                <OptionListBuilder
                                    v-if="showOptionListBuilder()"
                                    ref="optionListBuilder"
                                    v-model="focusFieldtype.option_list"
                                    @changed="changed = true"
                                />
                                <div
                                    v-if="focusFieldtype.atomic_type == 'regex'"
                                >
                                    <v-card>
                                    <v-card-text>
                                        <v-select
                                            v-if="!focusFieldtype.id"
                                            :items="GlobalRegexPatterns"
                                            label="Select a pattern"
                                            item-text="name"
                                            return-object
                                            @change="updatePattern($event)"
                                        />
                                        <v-text-field
                                            v-model="focusFieldtype.mask_pattern"
                                            :disabled="disabledRegexFields"
                                            label="Mask Pattern"
                                        />
                                        <v-text-field
                                            v-model="focusFieldtype.regex_pattern"
                                            :disabled="disabledRegexFields"
                                            label="Regex Validation Pattern"
                                        />
                                        <v-text-field
                                            v-model="focusFieldtype.validation_message"
                                            :disabled="disabledRegexFields"
                                            label="Validation Error Message"
                                        />
                                    </v-card-text>
                                    </v-card>
                                </div>
                                <div
                                    v-if="focusFieldtype.atomic_type == null"
                                    style="margin-top: 5px"
                                >
                                    <h4>Subfield Types</h4>
                                    <Draggable
                                        v-model="focusFieldtype.subfieldtypes"
                                        :options="{draggable:'.drag-ft', handle:'.handle', animation: 150, easing: 'cubic-bezier(1, 0, 0, 1)'}"
                                    >
                                        <SubFieldType
                                            v-for="(subFieldType, index) in focusFieldtype.subfieldtypes"
                                            ref="subFieldTypes"
                                            :key="index"
                                            class="drag-ft"
                                            :id.sync="subFieldType.id"
                                            :atomicType.sync="subFieldType.atomic_type"
                                            :optionListId.sync="subFieldType.option_list"
                                            :text.sync="subFieldType.text"
                                            :required.sync="subFieldType.required"
                                            :validationMessage.sync="subFieldType.validation_message"
                                            :regexPattern.sync="subFieldType.regex_pattern"
                                            :maskPattern.sync="subFieldType.mask_pattern"
                                            @loading="$emit('loading', arguments[0])"
                                            @changed="changed = true"
                                        />
                                        <div slot="footer">
                                            <v-btn
                                                color="white"
                                                small
                                                @click="addSubFieldType"
                                            >
                                                Add
                                            </v-btn>
                                        </div>
                                    </draggable>
                                </div>
                            </v-card-text>
                        </v-card>
                      <div
                        v-else
                        style="display:flex;flex-direction: column;justify-content: center;align-items: center;align-content: center;min-height: 600px;width: 100%;margin:auto;"
                      >
                        <div
                          style="text-align:center;font-size: 20px;font-weight: 300;"
                        >
                          <b>Select a Fieldtype to edit or create a new Fieldtype</b><br />
                          <small>To use a Reusable Fieldtype, add a question to a Template and click "Select Reusable Fieldtype"</small>
                        </div>
                        <div
                          style="display: flex;padding: 10px;background: #ECEFF1;border-radius: 10px;border: 1px solid #78909C;margin-top: 25px;opacity:0.7;max-width:500px;margin: 20px;"
                        >
                          <v-icon
                            left
                            size="32"
                            style="align-self: flex-start;"
                          >
                            mdi-information
                          </v-icon>
                          <div>
                            <div
                              style="font-size: 16px;font-weight: 700;"
                            >
                              About Reusable Fieldtypes
                            </div>
                            <div
                              style="font-size: 12px;"
                            >
                              With Reusable Fieldtypes, you can build fieldtypes that can be reused across multiple Questions and Templates.<br /><br />
                              Updates to a Reusable Fieldtype apply to all instances of the Fieldtype, across all Templates.
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-flex>
                </v-layout>
            </v-card-text>
        </v-card>
        <v-dialog
            v-model="questionDialog"
            width="800"
        >
            <v-card
                v-if="focusFieldtype"
            >
                <v-toolbar
                    flat
                    dense
                    color="primary"
                    dark
                >
                    {{focusFieldtype.name}} Used In
                    <v-spacer />
                    <v-btn
                        fab
                        small
                        color="red"
                    >
                        <v-icon
                            small
                            @click="questionDialog = false"
                        >
                            close
                        </v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    The below table displays the questions where {{focusFieldtype.name}} is used.
                    <br />
                    <br />
                    <v-data-table
                        :headers="headers"
                        :items="focusFieldtype.questions"
                        class="elevation-1"
                    >
                        <template v-slot:items="props">
                            <td>
                                <router-link :to="{ name: 'FormTemplate', params: { id: props.item.template.id } }">
                                    {{ props.item.template.name }}
                                </router-link>
                                <br />
                                <em>
                                    by {{props.item.template.created_by_name}}
                                </em>
                            </td>
                            <td>
                                <span
                                    v-if="props.item.section.number"
                                >
                                    {{ props.item.section.number }}.&nbsp;
                                </span>
                                <span
                                    v-if="props.item.section.text"
                                >
                                    {{ props.item.section.text }}
                                </span>
                            </td>
                            <td>
                                <span
                                    v-if="props.item.number"
                                >
                                    {{ props.item.number }}.&nbsp;
                                </span>
                                <span
                                    v-if="props.item.text"
                                >
                                    {{ props.item.text }}
                                </span>
                                <span
                                    v-if="!props.item.text && !props.item.number"
                                >
                                    <em>
                                        Untitled Question
                                    </em>
                                </span>
                                <span
                                    v-if="props.item.is_archived"
                                >
                                    <br />
                                    <b>
                                        Archived
                                    </b>
                                </span>
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import api from '@/api'
import GlobalConstants from '@/globals'
import Draggable from 'vuedraggable'
import OptionListBuilder from '@/components/admin/formtemplate/OptionListBuilder'
import SubFieldType from '@/components/admin/formtemplate/SubFieldType'
import { mapActions } from 'vuex'

export default {
    components: {
        Draggable,
        OptionListBuilder,
        SubFieldType
    },
    data () {
        return {
            fieldtypes: null,
            headers: [
                { text: 'Template', width: 250 },
                { text: 'Section', width: 250 },
                { text: 'Question Text', width: 250 },
            ],
            focusFieldtype: null,
            changed: false,
            loading: false,
            loadingFt: false,
            saving: false,
            search: null,
            page: 1,
            canNext: false,
            canPrev: false,
            total: 0,
            questionDialog: false,
            GlobalRegexPatterns: GlobalConstants.GlobalRegexPatterns,
            disabledRegexFields: false,
            atomicTypeOptions: GlobalConstants.AtomicFieldTypes.concat([
                {name: 'Custom', value: null}
            ]),
        }
    },
    created () {
        if (this.$route.params.id) {
            this.getFt(this.$route.params.id)
        }
        this.getFieldtypes()
        this.documentTitle('Reusable Fieldtypes')
    },
    methods: {
        ...mapActions('formtemplate', [
            'saveFieldType'
        ]),
        templateCount(questions) {
            var temps = []
            questions.forEach((q) => {
                if (!temps.includes(q['template']['id']))
                    temps.push(q['template']['id'])
            })
            return temps.length
        },
        goPage (int) {
            if (int < 0 && this.canPrev || int > 0 && this.canNext) {
                this.page = this.page + int
                this.getFieldtypes(this.search)
            }
        },
        updatePattern (event) {
            this.focusFieldtype.validation_message = event.validation_message
            this.focusFieldtype.regex_pattern = event.regex_pattern
            this.focusFieldtype.mask_pattern = event.mask_pattern
            this.disabledRegexFields = event.name !== "Custom Pattern"
        },
        getFieldtypes (named = null) {
            this.loading = true
            api.formtemplate.fieldType.named(named, this.page)
                .then((data) => {
                    this.total = data['count']
                    this.canNext = data['next_page'] !== null
                    this.canPrev = data['previous_page'] !== null
                    this.fieldtypes = data['results']
                    this.loading = false
                })
        },
        getFt (id) {
            this.loadingFt = true
            api.formtemplate.fieldType.get(id)
                .then((data) => {
                    this.focusFieldtype = data
                    this.changed = false
                })
        },
        showOptionListBuilder () {
            return (this.focusFieldtype.atomic_type === 'dropdown' || this.focusFieldtype.atomic_type === 'checkbox' || this.focusFieldtype.atomic_type === 'radio')
        },
        addSubFieldType () {
            this.focusFieldtype.subfieldtypes.push(
                {
                    id: null,
                    atomic_type: 'text',
                    text: null,
                    required: false
                }
            )
        },
        saveOptionList () {
            if (this.showOptionListBuilder()) {
                return this.$refs.optionListBuilder.save()
            } else {
                return Promise.resolve()
            }
        },
        saveFt () { 
            this.loadingFt = true
            return this.saveOptionList()
                .then(() => {
                    try {
                        return (this.focusFieldtype.atomic_type == null) ? Promise.all(
                            this.$refs.subFieldTypes.map(subFieldType => subFieldType.save())
                        ) : Promise.resolve()
                    } catch (e) {
                        // eslint-disable-next-line
                        console.log(e)
                    }
                })
                .then(() => {
                    return this.saveFieldType({
                        id: this.focusFieldtype.id,
                        name: this.focusFieldtype.name,
                        desc: this.focusFieldtype.desc,
                        atomic_type: this.focusFieldtype.atomic_type,
                        option_list: this.focusFieldtype.option_list,
                        subfieldtypes: this.focusFieldtype.subfieldtypes,
                        mask_pattern: this.focusFieldtype.mask_pattern !== "" ? this.focusFieldtype.mask_pattern : null,
                        regex_pattern: this.focusFieldtype.regex_pattern !== "" ? this.focusFieldtype.regex_pattern : null,
                        validation_message: this.focusFieldtype.validation_message !== "" ? this.focusFieldtype.validation_message : null,
                        required: this.focusFieldtype.required
                    })
                    .then(fieldType => {
                        this.changed = false
                        if ('id' in this.focusFieldtype) {
                            var ft = this.fieldtypes.find(x => x.id == fieldType.id)
                            var idx = this.fieldtypes.indexOf(ft)
                            this.fieldtypes[idx] = fieldType
                            this.$forceUpdate()
                            this.getFt(this.focusFieldtype.id)
                        } else {
                            this.$router.replace({name: 'OrgSelectedFieldtype', params: {id: fieldType.id}})
                        }
                    })
                })
        }
    },
    async beforeRouteUpdate(to, from, next) {
        if (this.changed) {
            const answer = window.confirm('Are you sure you want to leave? You have unsaved changes!')
            if (!answer)
                return false
        }
        next()
    },
    beforeRouteLeave (to, from, next) {
        if (this.changed) {
            const answer = window.confirm('Are you sure you want to leave? You have unsaved changes!')
            if (!answer)
                return false
        }
        next()
    },
    watch: {   
        $route (to, from) {
            this.getFieldtypes(this.search)
            if (to.params.id == "new") {
                this.loadingFt = true
                this.focusFieldtype = {
                    atomic_type: 'text',
                    subfieldtypes: []
                }
                this.changed = false
                this.loadingFt = false
            }
            else if (to.params.id) {
                this.getFt(to.params.id)
            } 
            else {
                this.focusFieldtype = null
            } 
        },
        search () {
            this.page = 1
            this.getFieldtypes(this.search)
        },
        focusFieldtype: {
            handler(newValue, oldValue) {
                if (this.loadingFt || !this.$route.params.id) {
                    this.changed = false
                    this.loadingFt = false
                }
                else
                    this.changed = true
            },
            deep: true,
            flush: 'post'
        }
    }
}
</script>

<style>

</style>